/**
 * Automatic Sync Integration Layer
 * 
 * Provides automatic integration of caching, optimistic updates, and entity sync
 * without requiring manual component-level changes. All synchronization happens
 * transparently through middleware and interceptors.
 */

import { Socket } from 'socket.io-client';
import CacheManager from './CacheManager';
import OptimisticUpdateManager from './OptimisticUpdateManager';
import EntitySyncManager from './EntitySyncManager';
import SocketCacheSync from '../services/SocketCacheSync';

export interface AutomaticSyncConfig {
  socket: Socket;
  enableAutoCache?: boolean;
  enableAutoOptimistic?: boolean;
  enableAutoEntitySync?: boolean;
  enableAutoConflictResolution?: boolean;
}

export interface APIEndpointConfig {
  cacheCategory?: 'chunks' | 'player' | 'market' | 'ui';
  optimisticType?: 'player' | 'inventory' | 'equipment' | 'stats' | 'custom';
  ttl?: number;
}

export class AutomaticSyncIntegration {
  private static instance: AutomaticSyncIntegration;
  private socket: Socket | null = null;
  private isInitialized = false;
  private apiInterceptors: Map<string, APIEndpointConfig> = new Map();

  private constructor() {}

  public static getInstance(): AutomaticSyncIntegration {
    if (!AutomaticSyncIntegration.instance) {
      AutomaticSyncIntegration.instance = new AutomaticSyncIntegration();
    }
    return AutomaticSyncIntegration.instance;
  }

  /**
   * Initialize automatic sync integration
   */
  public initialize(config: AutomaticSyncConfig): void {
    if (this.isInitialized) {
      console.warn('⚠️ AutomaticSyncIntegration already initialized');
      return;
    }

    this.socket = config.socket;
    this.isInitialized = true;

    console.log('🔄 AutomaticSyncIntegration initialized');

    // Initialize SocketCacheSync for automatic cache invalidation
    if (config.enableAutoCache !== false) {
      this.initializeAutomaticCacheSync();
    }

    // Setup automatic entity registration
    if (config.enableAutoEntitySync !== false) {
      this.setupAutomaticEntitySync();
    }

    // Setup automatic conflict resolution
    if (config.enableAutoConflictResolution !== false) {
      this.setupAutomaticConflictResolution();
    }
  }

  /**
   * Initialize automatic cache synchronization
   */
  private initializeAutomaticCacheSync(): void {
    if (!this.socket) return;

    SocketCacheSync.getInstance().initialize({
      socket: this.socket,
      enableWorldSync: true,
      enablePlayerSync: true,
      enableEntitySync: true,
      enableMarketSync: true,
    });

    console.log('✅ Automatic cache sync enabled');
  }

  /**
   * Setup automatic entity registration from socket events
   */
  private setupAutomaticEntitySync(): void {
    if (!this.socket) return;

    // Auto-register player entity
    this.socket.on('ships:update', (data: { ships: any[] }) => {
      const localShip = data.ships.find((s) => s.ownerId === this.socket?.id);
      if (localShip) {
        EntitySyncManager.registerEntity('player_ship', 'ship', localShip, 1);
      }
    });

    // Auto-register NPC entities
    this.socket.on('entity:spawned', (data: { entityId: string; type: string; data: any }) => {
      EntitySyncManager.registerEntity(data.entityId, data.type as any, data.data, 1);
    });

    // Auto-apply delta updates
    this.socket.on('entity:deltaUpdate', (data: { entityId: string; version: number; changes: Record<string, any>; timestamp: number }) => {
      EntitySyncManager.applyDelta(data.entityId, {
        entityId: data.entityId,
        version: data.version,
        changes: data.changes,
        timestamp: data.timestamp,
      });
    });

    console.log('✅ Automatic entity sync enabled');
  }

  /**
   * Setup automatic conflict resolution
   */
  private setupAutomaticConflictResolution(): void {
    if (!this.socket) return;

    this.socket.on('entity:conflict', (data: { entityId: string; clientVersion: number; serverVersion: number; serverData: any }) => {
      EntitySyncManager.resolveConflict(
        data.entityId,
        data.clientVersion,
        data.serverVersion,
        data.serverData,
        'server-wins' // Default strategy: server wins
      );
    });

    console.log('✅ Automatic conflict resolution enabled');
  }

  /**
   * Wrap API call with automatic optimistic update
   */
  public wrapAPICall<T>(
    key: string,
    type: 'player' | 'inventory' | 'equipment' | 'stats' | 'custom',
    apiCall: () => Promise<T>,
    getOptimisticData?: () => any,
    getOriginalData?: () => any
  ): Promise<T> {
    return new Promise(async (resolve, reject) => {
      try {
        // Get original data before making request
        const originalData = getOriginalData?.();

        // Get optimistic data if provided
        const optimisticData = getOptimisticData?.();

        // Create optimistic update if data provided
        if (optimisticData && originalData) {
          const serverPromise = apiCall();
          OptimisticUpdateManager.createUpdate(
            key,
            type,
            optimisticData,
            originalData,
            serverPromise,
            {
              onSuccess: (data) => {
                console.log(`✅ API call confirmed: ${key}`);
              },
              onFailure: (error) => {
                console.error(`❌ API call failed: ${key}`, error);
              },
            }
          );

          // Wait for server response
          const result = await serverPromise;
          resolve(result);
        } else {
          // No optimistic update, just make the call
          const result = await apiCall();
          resolve(result);
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Automatically cache data from API responses
   */
  public autoCacheResponse<T>(
    key: string,
    category: 'chunks' | 'player' | 'market' | 'ui',
    data: T
  ): void {
    try {
      switch (category) {
        case 'chunks':
          CacheManager.setChunk(key, data as any);
          break;
        case 'player':
          CacheManager.setPlayerData(key, data as any);
          break;
        case 'market':
          CacheManager.setMarketData(key, data as any);
          break;
        case 'ui':
          CacheManager.setUIState(key, data);
          break;
      }
      console.log(`💾 Auto-cached: ${category}/${key}`);
    } catch (error) {
      console.error(`⚠️ Failed to auto-cache: ${category}/${key}`, error);
    }
  }

  /**
   * Get cached data automatically
   */
  public getAutoCache<T>(
    key: string,
    category: 'chunks' | 'player' | 'market' | 'ui'
  ): T | null {
    try {
      switch (category) {
        case 'chunks':
          return CacheManager.getChunk(key) as T;
        case 'player':
          return CacheManager.getPlayerData(key) as T;
        case 'market':
          return CacheManager.getMarketData(key) as T;
        case 'ui':
          return CacheManager.getUIState(key) as T;
      }
    } catch (error) {
      console.error(`⚠️ Failed to get auto-cache: ${category}/${key}`, error);
    }
    return null;
  }

  /**
   * Register API endpoint for automatic optimization
   */
  public registerAPIEndpoint(
    endpoint: string,
    options: {
      cacheCategory?: 'chunks' | 'player' | 'market' | 'ui';
      optimisticType?: 'player' | 'inventory' | 'equipment' | 'stats' | 'custom';
      ttl?: number;
    }
  ): void {
    this.apiInterceptors.set(endpoint, options);
    console.log(`📍 Registered API endpoint: ${endpoint}`);
  }

  /**
   * Get sync statistics
   */
  public getSyncStats() {
    return {
      socketCacheSync: SocketCacheSync.getInstance().getStats(),
      optimisticUpdates: OptimisticUpdateManager.getStats(),
      entitySync: EntitySyncManager.getStats(),
      registeredEndpoints: this.apiInterceptors.size,
    };
  }

  /**
   * Cleanup
   */
  public disconnect(): void {
    this.socket = null;
    this.isInitialized = false;
    this.apiInterceptors.clear();
    console.log('🔄 AutomaticSyncIntegration disconnected');
  }
}

export default AutomaticSyncIntegration;
